import type { CellContext } from '@tanstack/react-table';
import { AlertCircle } from 'lucide-react';
import React, { useState, useEffect, useCallback, memo } from 'react';
import { toast } from 'sonner';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type {
  IOneItemInPrepareForRequest,
  ITodayOffersForRequest,
} from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface.ts';

// Constants and pre-defined styles to avoid recreation on each render
const MAX_PRICE_DIFF = 0.5;

// Toast styles
const TOAST_STYLES = {
  style: {
    background: '#FEF2F2',
    color: '#B91C1C',
    padding: '14px 18px',
    borderRadius: '8px',
    boxShadow: '0 10px 15px rgba(220, 38, 38, 0.1)',
    border: '1px solid rgba(220, 38, 38, 0.3)',
    maxWidth: '420px',
    fontWeight: 600,
  },
  iconTheme: {
    primary: '#EF4444',
    secondary: '#FFFFFF',
  },
  position: 'bottom-right' as const,
  duration: 4000,
};

const STYLES = {
  container: {
    base: 'group relative flex h-16 w-32 items-center justify-center rounded-md border',
    enabled:
      'cursor-pointer bg-white dark:bg-gray-900 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/70 hover:border-slate-300 dark:hover:border-slate-600',
    disabled:
      'cursor-not-allowed bg-slate-100 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700',
    active:
      'bg-indigo-50 dark:bg-indigo-900/10 border-indigo-200 dark:border-indigo-800/40',
  },
  input: {
    base: 'h-9 w-28 border-2 text-center text-lg font-medium rounded-md',
    enabled:
      'border-indigo-300 bg-white text-slate-800 dark:border-indigo-700/40 dark:bg-slate-900 dark:text-slate-200 focus:border-indigo-400 dark:focus:border-indigo-600 focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-800/30',
    disabled:
      'border-slate-300 bg-slate-100 text-slate-400 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-500',
    active:
      'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/10 dark:border-indigo-700/40',
  },
  display: {
    base: 'flex h-9 w-28 items-center justify-center rounded-md text-lg font-medium border',
    enabled:
      'text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900',
    disabled:
      'text-slate-400 dark:text-slate-500 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30',
    active:
      'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-300 dark:border-indigo-800/50',
  },
  warningOverlay: {
    base: 'absolute inset-0 flex items-center justify-center rounded-md border',
    light:
      'border-amber-300/50 bg-gradient-to-r from-amber-50/70 to-amber-100/70',
    dark: 'border-amber-800/50 dark:from-amber-900/20 dark:to-amber-800/10',
  },
  warningIcon: {
    base: 'flex h-8 w-8 items-center justify-center rounded-full border shadow-sm',
    light: 'border-amber-200 bg-amber-100 text-amber-600',
    dark: 'dark:border-amber-800/70 dark:bg-amber-900/70 dark:text-amber-300',
  },
};

const QtyEditingCellComponent = ({
  getValue,
  row: { index, original },
  column: { id },
  table,
}: CellContext<IOneItemInPrepareForRequest, unknown>) => {
  // Initial value and derived states for the cell
  const initialValue = getValue();

  // Extract calculations for readability and performance
  const price = original?.Price ?? 0;
  const healthyItemPrice = original?.healthyItemPrice ?? 0;
  const priceDiff = Math.abs(price - healthyItemPrice);
  const hasHealthyItemID = Boolean(original?.HealthyItemID);
  const isPriceAcceptable = priceDiff <= MAX_PRICE_DIFF;

  // Determine if the cell is disabled
  const isDisabled = !(hasHealthyItemID && isPriceAcceptable);

  // Create message for disabled cell - computed once per render
  const disabledMessage = !hasHealthyItemID
    ? 'الصنف غير مربوط بصنف صحيح في هلثي'
    : !isPriceAcceptable
      ? `السعر غير متطابق (اقصي فرق مقبول ${MAX_PRICE_DIFF} جنيه)`
      : '';

  // Local state for cell value and total value
  const [value, setValue] = useState<number>(Number(initialValue));
  const [totalValue, setTotalValue] = useState<number>(
    Number(original?.TotalAfterDiscount ?? 0),
  );

  // State to determine if the cell is in edit mode
  const [isEditing, setIsEditing] = useState(false);

  // Calculate total value based on current value - memoized function
  const calculateTotalValue = useCallback(
    (qty: number) => {
      const discount = original?.DiscountEdit ?? 0;
      return healthyItemPrice * qty * ((100 - discount) / 100);
    },
    [healthyItemPrice, original?.DiscountEdit],
  );

  // When focus is lost, update table data and exit edit mode
  const onBlur = useCallback(() => {
    const finalValue = value > 0 ? value : 0;

    // Update the quantity value
    table.options.meta?.updateData(index, id, finalValue);

    // Update the total after discount
    table.options.meta?.updateData(index, 'TotalAfterDiscount', totalValue);

    // Force a state update to trigger UI refresh for summary
    if (finalValue > 0) {
      // This redundant update will force a table re-render
      // and ensure the summary updates
      table.options.meta?.updateData(index, 'Qty', finalValue);
    }

    setIsEditing(false);
  }, [index, id, table.options.meta, totalValue, value]);

  // Keep local state synchronized with external changes
  useEffect(() => {
    setValue(Number(initialValue));
    setTotalValue(Number(original?.TotalAfterDiscount ?? 0));
  }, [initialValue, original?.TotalAfterDiscount]);

  // Handle input field changes
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputVal = e.target.value;

      if (inputVal === '') {
        setValue(0);
        setTotalValue(0);
        return;
      }

      const newValue = Number(inputVal);

      // Allow only non-negative values
      if (newValue < 0) return;

      setValue(newValue);
      setTotalValue(calculateTotalValue(newValue));
    },
    [calculateTotalValue],
  );

  // Handle cell click
  const handleCellClick = useCallback(() => {
    if (!isEditing && !isDisabled) {
      setIsEditing(true);
    } else if (isDisabled) {
      toast.error(`⚠️ ${disabledMessage}`, {
        ...TOAST_STYLES,
        style: {
          ...TOAST_STYLES.style,
          textAlign: 'right',
          direction: 'rtl',
        },
      });
    }
  }, [isEditing, isDisabled, disabledMessage]);

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === 'Enter' || e.key === ' ') && !isEditing && !isDisabled) {
        setIsEditing(true);
      }
    },
    [isEditing, isDisabled],
  );

  // Handle keyboard events during editing
  const handleEditingKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onBlur();
      }
    },
    [onBlur],
  );

  const hasValue = value > 0;

  // Compute container class based on state
  const containerClass = cn(
    STYLES.container.base,
    isDisabled ? STYLES.container.disabled : STYLES.container.enabled,
    hasValue && !isDisabled && STYLES.container.active,
  );

  // Compute input class based on state
  const inputClass = cn(
    STYLES.input.base,
    isDisabled ? STYLES.input.disabled : STYLES.input.enabled,
    hasValue && !isDisabled && STYLES.input.active,
  );

  // Compute display class based on state
  const displayClass = cn(
    STYLES.display.base,
    isDisabled ? STYLES.display.disabled : STYLES.display.enabled,
    hasValue && !isDisabled && STYLES.display.active,
  );

  // Compute warning overlay class
  const warningOverlayClass = cn(
    STYLES.warningOverlay.base,
    STYLES.warningOverlay.light,
    STYLES.warningOverlay.dark,
  );

  return (
    <div
      className={containerClass}
      role="button"
      tabIndex={0}
      onClick={handleCellClick}
      onKeyDown={handleKeyDown}
    >
      {isEditing ? (
        <Input
          id={`qty-${index}`}
          name={`qty-${index}`}
          autoFocus
          disabled={isDisabled}
          className={inputClass}
          type="number"
          value={value > 0 ? value : ''}
          onChange={handleChange}
          onBlur={onBlur}
          onKeyDown={handleEditingKeyDown}
        />
      ) : (
        <div className={displayClass}>{value > 0 ? value : '-'}</div>
      )}
      {isDisabled && (
        <div className={warningOverlayClass}>
          <div
            className={cn(
              STYLES.warningIcon.base,
              STYLES.warningIcon.light,
              STYLES.warningIcon.dark,
            )}
          >
            <AlertCircle className="h-4 w-4" />
          </div>
        </div>
      )}
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
const QtyEditingCell = memo(QtyEditingCellComponent);

export default QtyEditingCell;

export interface RequestFinalData {
  healthyRequestID: string | null;
  RequestID: string | null;
  RequestDone: boolean;
}

export interface FinalStageProps {
  isOpen: boolean;
  closeFn: () => void;
  items: IOneItemInPrepareForRequest[];
  client: ITodayOffersForRequest;
  closeDialogFinalFn: () => void;
  handleLoadingStage: (
    value: boolean,
    type: 'check' | 'upload' | 'update' | 'main',
  ) => void;
  isLoading: boolean;
}
